'use client';

import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { Check, X, Eye, AlertTriangle, ChevronUp, ChevronDown } from 'lucide-react';
import { useRole } from '@/context/RoleContext';

type TimesheetStatus = 'pending' | 'approved' | 'rejected' | 'clocked-in';

interface Timesheet {
  id: string;
  workerName: string;
  niNumber: string;
  niCategory: string;
  shiftDate: string;
  clockIn: string;
  clockOut: string;
  breakMins: number;
  totalHours: number;
  weeklyHours: number;
  payRate: number;
  gross: number;
  status: TimesheetStatus;
  wtdFlag: boolean;
  otFlag: boolean;
}

// Backend integration point: replace with API call to /api/timesheets?periodId=week-18
const TIMESHEETS: Timesheet[] = [
  { id: 'ts-001', workerName: 'Amara Okafor', niNumber: 'NJ 47 23 91 A', niCategory: 'A', shiftDate: '28/04/2026', clockIn: '07:00', clockOut: '15:30', breakMins: 30, totalHours: 8.0, weeklyHours: 38.5, payRate: 12.50, gross: 100.00, status: 'approved', wtdFlag: false, otFlag: false },
  { id: 'ts-002', workerName: 'Cian Walsh', niNumber: 'SN 82 14 55 C', niCategory: 'A', shiftDate: '28/04/2026', clockIn: '06:30', clockOut: '17:00', breakMins: 30, totalHours: 10.0, weeklyHours: 42.0, payRate: 12.50, gross: 125.00, status: 'pending', wtdFlag: false, otFlag: true },
  { id: 'ts-003', workerName: 'Priya Nair', niNumber: 'KP 33 67 12 B', niCategory: 'B', shiftDate: '29/04/2026', clockIn: '08:00', clockOut: '14:00', breakMins: 0, totalHours: 6.0, weeklyHours: 30.0, payRate: 11.44, gross: 68.64, status: 'approved', wtdFlag: false, otFlag: false },
  { id: 'ts-004', workerName: 'David Mensah', niNumber: 'TM 91 44 78 A', niCategory: 'A', shiftDate: '29/04/2026', clockIn: '09:00', clockOut: '17:00', breakMins: 60, totalHours: 7.0, weeklyHours: 30.0, payRate: 12.50, gross: 87.50, status: 'pending', wtdFlag: false, otFlag: false },
  { id: 'ts-005', workerName: 'Fatima Byrne', niNumber: 'PW 56 31 04 A', niCategory: 'A', shiftDate: '28/04/2026', clockIn: '06:00', clockOut: '14:30', breakMins: 30, totalHours: 8.0, weeklyHours: 40.0, payRate: 13.20, gross: 105.60, status: 'approved', wtdFlag: false, otFlag: true },
  { id: 'ts-006', workerName: 'Grace Adeyemi', niNumber: 'LR 72 88 36 A', niCategory: 'A', shiftDate: '30/04/2026', clockIn: '07:30', clockOut: '13:30', breakMins: 0, totalHours: 6.0, weeklyHours: 28.0, payRate: 11.44, gross: 68.64, status: 'approved', wtdFlag: false, otFlag: false },
  { id: 'ts-007', workerName: 'Hassan Clarke', niNumber: 'MN 19 52 67 A', niCategory: 'A', shiftDate: '30/04/2026', clockIn: '14:00', clockOut: '22:00', breakMins: 30, totalHours: 7.5, weeklyHours: 36.5, payRate: 12.50, gross: 93.75, status: 'pending', wtdFlag: false, otFlag: false },
  { id: 'ts-008', workerName: 'Ifeoma Obi', niNumber: 'QS 64 27 89 A', niCategory: 'A', shiftDate: '01/05/2026', clockIn: '07:00', clockOut: '19:00', breakMins: 60, totalHours: 11.0, weeklyHours: 47.5, payRate: 12.50, gross: 137.50, status: 'pending', wtdFlag: true, otFlag: true },
  { id: 'ts-009', workerName: 'James Whitfield', niNumber: 'BR 48 93 15 C', niCategory: 'C', shiftDate: '01/05/2026', clockIn: '09:00', clockOut: '13:00', breakMins: 0, totalHours: 4.0, weeklyHours: 16.0, payRate: 11.44, gross: 45.76, status: 'approved', wtdFlag: false, otFlag: false },
  { id: 'ts-010', workerName: 'Kezia Mwangi', niNumber: 'XN 37 61 43 A', niCategory: 'A', shiftDate: '02/05/2026', clockIn: '06:00', clockOut: '14:00', breakMins: 30, totalHours: 7.5, weeklyHours: 37.5, payRate: 12.50, gross: 93.75, status: 'approved', wtdFlag: false, otFlag: false },
  { id: 'ts-011', workerName: 'Liam O\'Sullivan', niNumber: 'HJ 82 14 72 A', niCategory: 'A', shiftDate: '03/05/2026', clockIn: '08:00', clockOut: '16:00', breakMins: 30, totalHours: 7.5, weeklyHours: 35.0, payRate: 12.50, gross: 93.75, status: 'rejected', wtdFlag: false, otFlag: false },
  { id: 'ts-012', workerName: 'Miriam Asante', niNumber: 'WP 55 39 08 B', niCategory: 'B', shiftDate: '04/05/2026', clockIn: '07:15', clockOut: '--', breakMins: 0, totalHours: 0, weeklyHours: 0, payRate: 12.50, gross: 0, status: 'clocked-in', wtdFlag: false, otFlag: false },
];

type SortKey = 'workerName' | 'shiftDate' | 'totalHours' | 'gross' | 'status';

export default function TimesheetTable() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('shiftDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [timesheets, setTimesheets] = useState<Timesheet[]>(TIMESHEETS);
  const { permissions } = useRole();

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === timesheets.length) setSelected(new Set());
    else setSelected(new Set(timesheets.map((t) => t.id)));
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const handleApprove = (id: string) => {
    setTimesheets((prev) =>
      prev.map((t) => (t.id === id ? { ...t, status: 'approved' as TimesheetStatus } : t))
    );
  };

  const handleReject = (id: string) => {
    setTimesheets((prev) =>
      prev.map((t) => (t.id === id ? { ...t, status: 'rejected' as TimesheetStatus } : t))
    );
  };

  const sorted = [...timesheets].sort((a, b) => {
    let av: string | number = a[sortKey];
    let bv: string | number = b[sortKey];
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="inline-flex flex-col ml-1">
      <ChevronUp size={9} className={sortKey === col && sortDir === 'asc' ? 'text-primary' : 'text-muted-foreground/40'} />
      <ChevronDown size={9} className={sortKey === col && sortDir === 'desc' ? 'text-primary' : 'text-muted-foreground/40'} />
    </span>
  );

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-[13px] min-w-[1100px]">
          <thead>
            <tr className="border-b border-border bg-secondary/40">
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selected.size === timesheets.length && timesheets.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-border accent-primary cursor-pointer"
                  aria-label="Select all timesheets"
                />
              </th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('workerName')}>
                Worker <SortIcon col="workerName" />
              </th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground">
                NI Number
              </th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground">
                Cat.
              </th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('shiftDate')}>
                Shift Date <SortIcon col="shiftDate" />
              </th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Clock In</th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Clock Out</th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Break</th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('totalHours')}>
                Shift Hrs <SortIcon col="totalHours" />
              </th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Wk Hrs</th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Rate/hr</th>
              <th className="px-4 py-3 text-right font-600 text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('gross')}>
                Gross <SortIcon col="gross" />
              </th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('status')}>
                Status <SortIcon col="status" />
              </th>
              <th className="px-4 py-3 text-center font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map((ts) => (
              <tr
                key={ts.id}
                className={`group transition-colors duration-100 ${
                  selected.has(ts.id) ? 'bg-primary/5' : 'hover:bg-secondary/50'
                } ${ts.wtdFlag ? 'bg-danger/3' : ''}`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(ts.id)}
                    onChange={() => toggleSelect(ts.id)}
                    className="rounded border-border accent-primary cursor-pointer"
                    aria-label={`Select ${ts.workerName}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-700 text-primary">
                        {ts.workerName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                    <span className="font-500 text-foreground whitespace-nowrap">{ts.workerName}</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-[12px] text-muted-foreground whitespace-nowrap">{ts.niNumber}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-secondary text-[11px] font-700 text-foreground">
                    {ts.niCategory}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{ts.shiftDate}</td>
                <td className="px-4 py-3 font-mono text-[12px] text-foreground">{ts.clockIn}</td>
                <td className="px-4 py-3 font-mono text-[12px] text-foreground">
                  {ts.clockOut === '--' ? (
                    <span className="text-success font-500">Live</span>
                  ) : ts.clockOut}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{ts.breakMins > 0 ? `${ts.breakMins}m` : '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono font-500 text-foreground tabular-nums">{ts.totalHours > 0 ? `${ts.totalHours}h` : '—'}</span>
                    {ts.otFlag && (
                      <span className="text-[10px] font-600 text-warning bg-warning/10 px-1 rounded">OT</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className={`font-mono text-[12px] tabular-nums ${ts.weeklyHours > 40 ? 'text-warning font-600' : 'text-foreground'}`}>
                      {ts.weeklyHours > 0 ? `${ts.weeklyHours}h` : '—'}
                    </span>
                    {ts.wtdFlag && (
                      <span title="Working Time Directive — approaching 48hr limit">
                        <AlertTriangle size={12} className="text-danger" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-[12px] text-muted-foreground tabular-nums">£{ts.payRate.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono font-600 text-foreground tabular-nums">
                  {ts.gross > 0 ? `£${ts.gross.toFixed(2)}` : '—'}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge variant={ts.status} size="sm" />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <button
                      className="relative p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-all duration-150 group/btn"
                      aria-label="View timesheet details"
                    >
                      <Eye size={14} />
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-foreground text-card text-[10px] rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 pointer-events-none transition-opacity z-20">
                        View details
                      </span>
                    </button>
                    {permissions.canApproveTimesheets && (ts.status === 'pending') && (
                      <>
                        <button
                          onClick={() => handleApprove(ts.id)}
                          className="relative p-1.5 rounded-md hover:bg-success/10 text-muted-foreground hover:text-success transition-all duration-150 group/btn"
                          aria-label="Approve timesheet"
                        >
                          <Check size={14} />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-foreground text-card text-[10px] rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 pointer-events-none transition-opacity z-20">
                            Approve timesheet
                          </span>
                        </button>
                        <button
                          onClick={() => handleReject(ts.id)}
                          className="relative p-1.5 rounded-md hover:bg-danger/10 text-muted-foreground hover:text-danger transition-all duration-150 group/btn"
                          aria-label="Reject timesheet"
                        >
                          <X size={14} />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-foreground text-card text-[10px] rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 pointer-events-none transition-opacity z-20">
                            Reject timesheet
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-secondary/20">
        <p className="text-[12px] text-muted-foreground">
          Showing <span className="font-500 text-foreground">12</span> of <span className="font-500 text-foreground">47</span> timesheets
          {selected.size > 0 && (
            <span className="ml-2 text-primary font-500">· {selected.size} selected</span>
          )}
        </p>
        <div className="flex items-center gap-1">
          {['1', '2', '3', '4'].map((p) => (
            <button
              key={`page-${p}`}
              className={`w-7 h-7 rounded text-[12px] font-500 transition-all duration-150 ${
                p === '1' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              {p}
            </button>
          ))}
          <span className="text-[12px] text-muted-foreground px-1">···</span>
          <button className="w-7 h-7 rounded text-[12px] font-500 text-muted-foreground hover:bg-secondary hover:text-foreground transition-all duration-150">
            5
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-muted-foreground">Rows per page</span>
          <select className="text-[12px] font-500 bg-card border border-border rounded px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/30">
            <option>12</option>
            <option>25</option>
            <option>50</option>
          </select>
        </div>
      </div>
    </div>
  );
}